import { useEffect, useState, useCallback } from 'react'
import { useBacklog } from '@/hooks/useBacklog'
import {
  ClipboardList, Plus, X, ChevronDown, Trash2, Edit3,
  AlertCircle, ArrowUp, Minus, ArrowDown, Bug, Sparkles,
  TrendingUp, Database, Palette, RefreshCw,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { clsx } from 'clsx'
import type {
  BacklogTask, BacklogTaskCreate, BacklogTaskUpdate,
  TaskStatus, TaskPriority, TaskCategory,
} from '@/lib/types'

// ── Constants ──────────────────────────────────────────────────────────────

const COLUMNS: { id: TaskStatus; label: string; color: string }[] = [
  { id: 'backlog',     label: 'Backlog',     color: 'text-text-muted'       },
  { id: 'todo',        label: 'To Do',       color: 'text-blue-light'       },
  { id: 'in_progress', label: 'In Progress', color: 'text-amber'            },
  { id: 'done',        label: 'Done',        color: 'text-emerald'          },
]

const PRIORITIES: { value: TaskPriority; label: string; icon: typeof AlertCircle; color: string; bg: string; border: string }[] = [
  { value: 'critical', label: 'Critical', icon: AlertCircle, color: 'text-rose-light', bg: 'bg-rose/10',   border: 'border-rose/30'   },
  { value: 'high',     label: 'High',     icon: ArrowUp,     color: 'text-amber',      bg: 'bg-amber/10',  border: 'border-amber/30'  },
  { value: 'medium',   label: 'Medium',   icon: Minus,       color: 'text-blue-light', bg: 'bg-blue/10',   border: 'border-blue/30'   },
  { value: 'low',      label: 'Low',      icon: ArrowDown,   color: 'text-text-muted', bg: 'bg-surface-card', border: 'border-border' },
]

const CATEGORIES: { value: TaskCategory; label: string; icon: typeof Bug; color: string }[] = [
  { value: 'bug',         label: 'Bug',         icon: Bug,        color: 'text-rose-light'      },
  { value: 'feature',     label: 'Feature',     icon: Sparkles,   color: 'text-purple-light'    },
  { value: 'improvement', label: 'Improvement', icon: TrendingUp, color: 'text-blue-light'      },
  { value: 'data',        label: 'Data',        icon: Database,   color: 'text-emerald'         },
  { value: 'ui',          label: 'UI',          icon: Palette,    color: 'text-amber'           },
]

const STATUS_LABELS: Record<TaskStatus, string> = {
  backlog:     'Backlog',
  todo:        'To Do',
  in_progress: 'In Progress',
  done:        'Done',
}

// ── Helpers ────────────────────────────────────────────────────────────────

function getPriority(v: TaskPriority) {
  return PRIORITIES.find(p => p.value === v) ?? PRIORITIES[2]
}
function getCategory(v: TaskCategory) {
  return CATEGORIES.find(c => c.value === v) ?? CATEGORIES[0]
}

// ── Priority Badge ─────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: TaskPriority }) {
  const p = getPriority(priority)
  const Icon = p.icon
  return (
    <span className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border', p.bg, p.border, p.color)}>
      <Icon size={9} aria-hidden />
      {p.label}
    </span>
  )
}

// ── Category Badge ─────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: TaskCategory }) {
  const c = getCategory(category)
  const Icon = c.icon
  return (
    <span className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-DEFAULT border border-border', c.color)}>
      <Icon size={9} aria-hidden />
      {c.label}
    </span>
  )
}

// ── Add / Edit Task Modal ──────────────────────────────────────────────────

interface TaskModalProps {
  initialStatus?: TaskStatus
  task?: BacklogTask | null
  onClose: () => void
  onSave: (data: BacklogTaskCreate | BacklogTaskUpdate) => Promise<void>
}

function TaskModal({ initialStatus = 'backlog', task, onClose, onSave }: TaskModalProps) {
  const [title,       setTitle]       = useState(task?.title       ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [status,      setStatus]      = useState<TaskStatus>(task?.status     ?? initialStatus)
  const [priority,    setPriority]    = useState<TaskPriority>(task?.priority ?? 'medium')
  const [category,    setCategory]    = useState<TaskCategory>(task?.category ?? 'feature')
  const [saving,      setSaving]      = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    await onSave({ title: title.trim(), description, status, priority, category })
    setSaving(false)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={task ? 'Edit task' : 'New task'}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-lg mx-4 card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <ClipboardList size={15} className="text-blue-light" aria-hidden />
            {task ? 'Edit Task' : 'New Task'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-surface-card text-text-muted hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Title <span className="text-rose-light">*</span>
            </label>
            <input
              autoFocus
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="What needs doing?"
              className="input"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="More detail, steps to reproduce, acceptance criteria…"
              rows={3}
              className="input resize-none"
            />
          </div>

          {/* Row: Status + Priority + Category */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value as TaskStatus)}
                className="input px-2 py-2 text-xs"
              >
                {COLUMNS.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value as TaskPriority)}
                className="input px-2 py-2 text-xs"
              >
                {PRIORITIES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value as TaskCategory)}
                className="input px-2 py-2 text-xs"
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost px-3 py-1.5 text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="btn-primary px-4 py-1.5 text-xs"
            >
              {saving ? 'Saving…' : task ? 'Save Changes' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Task Card ──────────────────────────────────────────────────────────────

interface TaskCardProps {
  task: BacklogTask
  onEdit: (task: BacklogTask) => void
  onDelete: (id: number) => void
  onMove: (id: number, status: TaskStatus) => void
}

function TaskCard({ task, onEdit, onDelete, onMove }: TaskCardProps) {
  const [moveOpen, setMoveOpen] = useState(false)

  const otherStatuses = COLUMNS.filter(c => c.id !== task.status)

  return (
    <div
      className="group card-hover p-3 cursor-pointer"
      onClick={() => onEdit(task)}
      role="button"
      aria-label={`Edit task: ${task.title}`}
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(task) } }}
    >
      {/* Title */}
      <p className="text-sm text-text-primary font-medium leading-snug mb-2 pr-4">
        {task.title}
      </p>

      {/* Description preview — screenshot markdown stripped from the text and
          shown as real thumbnails. Discord bug reports arrive as
          ![screenshot](url); a photo IS the report, so rendering the raw
          markdown would bury the one thing worth looking at. */}
      {task.description && (() => {
        const shots = [...task.description.matchAll(/!\[[^\]]*\]\(([^)]+)\)/g)]
          .map(m => m[1]).filter(Boolean)
        const text = task.description
          .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
          .replace(/\*\*Screenshots\*\*/g, '')
          .trim()
        return (
          <>
            {text && (
              <p className="text-xs text-text-muted line-clamp-2 mb-2 leading-relaxed">
                {text}
              </p>
            )}
            {shots.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                {shots.slice(0, 3).map((src, i) => (
                  <a key={src + i} href={src} target="_blank" rel="noreferrer"
                     onClick={e => e.stopPropagation()}
                     title="Open the full screenshot">
                    <img
                      src={src} alt={`Report screenshot ${i + 1}`} loading="lazy"
                      className="h-12 w-16 object-cover rounded border border-border
                                 hover:border-border-light transition-colors"
                    />
                  </a>
                ))}
                {shots.length > 3 && (
                  <span className="text-[10px] text-text-muted tabular-nums">
                    +{shots.length - 3}
                  </span>
                )}
              </div>
            )}
          </>
        )
      })()}

      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <PriorityBadge priority={task.priority} />
        <CategoryBadge category={task.category} />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-text-muted">
          {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
        </span>

        {/* Actions (visible on hover) */}
        <div
          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={e => e.stopPropagation()}
        >
          {/* Move dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMoveOpen(o => !o)}
              className="p-1 rounded hover:bg-surface-card text-text-muted hover:text-blue-light transition-colors"
              aria-label="Move to column"
              title="Move to…"
            >
              <ChevronDown size={12} aria-hidden />
            </button>
            {moveOpen && (
              <div className="absolute bottom-full right-0 mb-1 w-32 bg-surface-DEFAULT border border-border rounded-lg shadow-xl z-20 py-1">
                {otherStatuses.map(col => (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => { onMove(task.id, col.id); setMoveOpen(false) }}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-card hover:text-text-primary transition-colors"
                  >
                    {col.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Edit */}
          <button
            type="button"
            onClick={() => onEdit(task)}
            className="p-1 rounded hover:bg-surface-card text-text-muted hover:text-text-primary transition-colors"
            aria-label="Edit task"
            title="Edit"
          >
            <Edit3 size={12} aria-hidden />
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={() => onDelete(task.id)}
            className="p-1 rounded hover:bg-rose/10 text-text-muted hover:text-rose-light transition-colors"
            aria-label="Delete task"
            title="Delete"
          >
            <Trash2 size={12} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Kanban Column ──────────────────────────────────────────────────────────

interface ColumnProps {
  status: TaskStatus
  label: string
  colorClass: string
  tasks: BacklogTask[]
  onAddClick: () => void
  onEdit: (task: BacklogTask) => void
  onDelete: (id: number) => void
  onMove: (id: number, status: TaskStatus) => void
}

function KanbanColumn({ status, label, colorClass, tasks, onAddClick, onEdit, onDelete, onMove }: ColumnProps) {
  return (
    <div className="flex flex-col min-w-0 flex-1">
      {/* Column header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <h2 className={clsx('text-xs font-semibold uppercase tracking-wider', colorClass)}>
            {label}
          </h2>
          <span className="badge-neutral text-[10px] font-mono">
            {tasks.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onAddClick}
          className="p-1 rounded hover:bg-surface-card text-text-muted hover:text-blue-light transition-colors"
          aria-label={`Add task to ${label}`}
          title="Add task"
        >
          <Plus size={13} aria-hidden />
        </button>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-2 flex-1">
        {tasks.length === 0 ? (
          <div className="flex items-center justify-center h-16 border border-dashed border-border rounded-lg">
            <span className="text-xs text-text-muted">No tasks</span>
          </div>
        ) : (
          tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
            />
          ))
        )}
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function BacklogPage() {
  const { tasks, loading, error, fetchTasks, create, update, move, remove } = useBacklog()

  // Modal state
  const [modalOpen,      setModalOpen]      = useState(false)
  const [editingTask,    setEditingTask]     = useState<BacklogTask | null>(null)
  const [addingToStatus, setAddingToStatus] = useState<TaskStatus>('backlog')

  // Filters
  const [filterPriority, setFilterPriority] = useState<TaskPriority | 'all'>('all')
  const [filterCategory, setFilterCategory] = useState<TaskCategory | 'all'>('all')

  useEffect(() => { fetchTasks() }, [fetchTasks])

  const openAdd = useCallback((status: TaskStatus) => {
    setEditingTask(null)
    setAddingToStatus(status)
    setModalOpen(true)
  }, [])

  const openEdit = useCallback((task: BacklogTask) => {
    setEditingTask(task)
    setModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setEditingTask(null)
  }, [])

  const handleSave = useCallback(async (data: BacklogTaskCreate | BacklogTaskUpdate) => {
    if (editingTask) {
      await update(editingTask.id, data as BacklogTaskUpdate)
    } else {
      await create({ ...data, status: addingToStatus } as BacklogTaskCreate)
    }
  }, [editingTask, addingToStatus, create, update])

  const handleDelete = useCallback(async (id: number) => {
    if (window.confirm('Delete this task?')) {
      await remove(id)
    }
  }, [remove])

  const handleMove = useCallback(async (id: number, status: TaskStatus) => {
    await move(id, status)
  }, [move])

  // Filter tasks
  const filtered = tasks.filter(t => {
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false
    if (filterCategory !== 'all' && t.category !== filterCategory) return false
    return true
  })

  const byStatus = (status: TaskStatus) =>
    filtered.filter(t => t.status === status)

  // Summary counts
  const totalOpen = tasks.filter(t => t.status !== 'done').length
  const criticalCount = tasks.filter(t => t.priority === 'critical' && t.status !== 'done').length

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ClipboardList size={20} className="text-blue-light" aria-hidden />
            Backlog
          </h1>
          <p className="text-sm text-text-muted mt-0.5">
            {totalOpen} open tasks
            {criticalCount > 0 && (
              <span className="ml-2 text-rose-light font-medium">· {criticalCount} critical</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fetchTasks()}
            className="p-2 rounded-lg border border-border hover:bg-surface-card text-text-muted hover:text-text-primary transition-colors"
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={14} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => openAdd('backlog')}
            className="btn-primary text-xs gap-1.5"
          >
            <Plus size={13} aria-hidden />
            New Task
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl px-3 py-2 flex items-center gap-3 flex-wrap">
        <span className="text-xs text-text-muted">Filter:</span>

        {/* Priority filter */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setFilterPriority('all')}
            className={clsx(
              'px-2.5 py-1 rounded text-xs transition-colors',
              filterPriority === 'all'
                ? 'bg-blue/10 text-blue-light border border-blue/30'
                : 'text-text-muted hover:text-text-secondary border border-transparent',
            )}
          >
            All priorities
          </button>
          {PRIORITIES.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => setFilterPriority(filterPriority === p.value ? 'all' : p.value)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs border transition-colors',
                filterPriority === p.value
                  ? clsx(p.bg, p.border, p.color)
                  : 'text-text-muted hover:text-text-secondary border-transparent',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="w-px h-4 bg-border" />

        {/* Category filter */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setFilterCategory('all')}
            className={clsx(
              'px-2.5 py-1 rounded text-xs transition-colors',
              filterCategory === 'all'
                ? 'bg-blue/10 text-blue-light border border-blue/30'
                : 'text-text-muted hover:text-text-secondary border border-transparent',
            )}
          >
            All categories
          </button>
          {CATEGORIES.map(c => {
            const Icon = c.icon
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => setFilterCategory(filterCategory === c.value ? 'all' : c.value)}
                className={clsx(
                  'flex items-center gap-1 px-2.5 py-1 rounded text-xs border transition-colors',
                  filterCategory === c.value
                    ? clsx('bg-surface-card border-border', c.color)
                    : 'text-text-muted hover:text-text-secondary border-transparent',
                )}
              >
                <Icon size={10} aria-hidden />
                {c.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-rose/10 border border-rose/20 rounded-lg text-sm text-rose-light">
          <AlertCircle size={15} aria-hidden />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && tasks.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-40 skeleton" />
          ))}
        </div>
      )}

      {/* Kanban board */}
      {!loading || tasks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 stagger-children">
          {COLUMNS.map(col => (
            <KanbanColumn
              key={col.id}
              status={col.id}
              label={col.label}
              colorClass={col.color}
              tasks={byStatus(col.id)}
              onAddClick={() => openAdd(col.id)}
              onEdit={openEdit}
              onDelete={handleDelete}
              onMove={handleMove}
            />
          ))}
        </div>
      ) : null}

      {/* Modal */}
      {modalOpen && (
        <TaskModal
          initialStatus={addingToStatus}
          task={editingTask}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </div>
  )
}
